import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  MdKeyboardArrowDown,
  MdOutlineFileUpload,
  MdUploadFile,
  MdOutlineCollections,
  MdMoreVert,
} from "react-icons/md";
import toast from "react-hot-toast";

/**
 * Reusable Product Actions Menu component for Admin operations.
 * Uses React Portal to ensure the dropdown menu is never clipped or hidden behind cards.
 *
 * @param {Object} props
 * @param {Function} props.onSingleUpload - Triggered when Single Upload Product option is clicked
 * @param {Function} [props.onBulkProductUpload] - Optional handler for bulk product upload via CSV
 * @param {Function} [props.onBulkImageUpload] - Optional handler for bulk image upload
 * @param {string} [props.className] - Additional class names
 */
export default function ProductActionsMenu({
  onSingleUpload,
  onBulkProductUpload,
  onBulkImageUpload,
  className = "",
  buttonClassName = "",
}) {
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const actionsRef = useRef(null);
  const popoverRef = useRef(null);
  const [popoverStyle, setPopoverStyle] = useState({});

  const updatePopoverPosition = useCallback(() => {
    if (!actionsRef.current) return;
    const buttonRect = actionsRef.current.getBoundingClientRect();
    const width = 208; // w-52
    const gap = 6;
    const viewportPadding = 12;

    const preferredLeft = buttonRect.right - width;
    const left = Math.min(
      Math.max(viewportPadding, preferredLeft),
      window.innerWidth - width - viewportPadding
    );

    const top = buttonRect.bottom + gap;

    setPopoverStyle({
      position: "fixed",
      top,
      left,
      width,
      zIndex: 9999,
    });
  }, []);

  useEffect(() => {
    if (!isActionsOpen) return undefined;

    updatePopoverPosition();
    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", updatePopoverPosition, true);

    return () => {
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", updatePopoverPosition, true);
    };
  }, [isActionsOpen, updatePopoverPosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        actionsRef.current &&
        !actionsRef.current.contains(event.target) &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target)
      ) {
        setIsActionsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleBulkProducts = () => {
    setIsActionsOpen(false);
    if (onBulkProductUpload) {
      onBulkProductUpload();
    } else {
      toast.info("Bulk product upload feature via CSV.", { icon: "📁" });
    }
  };

  const handleBulkImages = () => {
    setIsActionsOpen(false);
    if (onBulkImageUpload) {
      onBulkImageUpload();
    } else {
      toast.info("Bulk image upload feature.", { icon: "🖼️" });
    }
  };

  return (
    <div className={`relative inline-block text-left ${className}`} ref={actionsRef}>
      {/* Actions Dropdown Button */}
      <button
        className={`app-button-secondary flex items-center justify-center gap-1.5 rounded-md h-8 w-8 sm:w-auto px-0 sm:px-3 transition-all text-xs font-semibold bg-white border border-slate-300 hover:border-slate-400 text-slate-700 shadow-sm focus:outline-none cursor-pointer shrink-0 ${buttonClassName}`}
        onClick={() => {
          if (!isActionsOpen) updatePopoverPosition();
          setIsActionsOpen((prev) => !prev);
        }}
        type="button"
        aria-expanded={isActionsOpen}
        aria-haspopup="true"
        title="Actions Menu"
      >
        {/* Mobile: 3 Vertical Dots */}
        <MdMoreVert size={18} className="sm:hidden text-slate-700 shrink-0" />

        {/* Desktop: Actions Label + Arrow */}
        <span className="hidden sm:inline">Actions</span>
        <MdKeyboardArrowDown
          size={16}
          className={`hidden sm:inline transition-transform duration-200 ${
            isActionsOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Actions Popup Menu via Portal */}
      {isActionsOpen &&
        createPortal(
          <div
            ref={popoverRef}
            style={popoverStyle}
            className="bg-white rounded-lg shadow-xl border border-slate-200/80 z-[9999] py-2 text-left animate-in fade-in slide-in-from-top-2 duration-150"
          >
            <div className="px-3 py-1 text-[10px] font-bold text-slate-400 tracking-wider uppercase border-b border-slate-100 mb-1">
              ADD / UPLOAD
            </div>

            {/* Bulk Upload Products */}
            <button
              type="button"
              onClick={handleBulkProducts}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2.5 transition-colors group cursor-pointer"
            >
              <div className="w-7 h-7 rounded-md bg-slate-100 group-hover:bg-primary/10 text-slate-600 group-hover:text-primary flex items-center justify-center shrink-0 mt-0.5 transition-colors">
                <MdUploadFile size={16} />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800 group-hover:text-primary transition-colors">
                  Bulk Upload Products
                </div>
                <div className="text-[10px] text-slate-500 font-medium leading-tight">
                  Make entries via CSV files
                </div>
              </div>
            </button>

            {/* Bulk Upload Images */}
            <button
              type="button"
              onClick={handleBulkImages}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-start gap-2.5 transition-colors group cursor-pointer"
            >
              <div className="w-7 h-7 rounded-md bg-slate-100 group-hover:bg-primary/10 text-slate-600 group-hover:text-primary flex items-center justify-center shrink-0 mt-0.5 transition-colors">
                <MdOutlineCollections size={16} />
              </div>
              <div>
                <div className="text-xs font-bold text-slate-800 group-hover:text-primary transition-colors">
                  Bulk Upload Images
                </div>
                <div className="text-[10px] text-slate-500 font-medium leading-tight">
                  Upload images in batches
                </div>
              </div>
            </button>
          </div>,
          document.body
        )}
    </div>
  );
}
